/**
 * Legal References Index
 *
 * Centralized access to all legal references used in Control Assistant
 * and Document Tracking mocks.
 */

import type { LegalReference } from '@hofsync/design-system';

// Import all legal reference modules
import { DUEV_LEGAL_REFS, DUEV_3, DUEV_4_1, DUEV_4_4, DUEV_6, DUEV_6_8, DUEV_10_1, DUEV_10_2 } from './duev';
import { STOFFBILV_LEGAL_REFS, STOFFBILV_3, STOFFBILV_6, STOFFBILV_7 } from './stoffbilv';
import {
  PFLANZENSCHUTZ_LEGAL_REFS,
  PFLSCHG_9,
  PFLSCHG_11,
  PFLSCHSACHKV_1,
  PFLSCHSACHKV_7,
} from './pflanzenschutz';
import {
  EQUIPMENT_SAFETY_LEGAL_REFS,
  STVZO_29,
  BETRSICHV_3,
  BETRSICHV_14,
  ARBSCHG_12,
  ENERGIESTG_57,
} from './equipment-safety';

/**
 * All legal references indexed by ID.
 */
export const ALL_LEGAL_REFS: Record<string, LegalReference> = {
  ...DUEV_LEGAL_REFS,
  ...STOFFBILV_LEGAL_REFS,
  ...PFLANZENSCHUTZ_LEGAL_REFS,
  ...EQUIPMENT_SAFETY_LEGAL_REFS,
};

/**
 * Get a legal reference by ID.
 *
 * @param id - The legal reference ID (e.g., 'duev-4-4')
 * @returns The legal reference or undefined if not found
 */
export function getLegalRef(id: string): LegalReference | undefined {
  return ALL_LEGAL_REFS[id];
}

/**
 * Get multiple legal references by IDs.
 *
 * @param ids - Array of legal reference IDs
 * @returns Array of found legal references (filters out undefined)
 */
export function getLegalRefs(ids: string[]): LegalReference[] {
  return ids.map((id) => getLegalRef(id)).filter((ref): ref is LegalReference => ref !== undefined);
}

/**
 * Get all legal references for a specific law.
 *
 * @param law - The law abbreviation (e.g., 'DüV', 'StoffBilV')
 * @returns Array of legal references from that law
 */
export function getLegalRefsByLaw(law: string): LegalReference[] {
  return Object.values(ALL_LEGAL_REFS).filter((ref) => ref.law === law);
}

/**
 * Get all legal references as a Record (for passing to components).
 *
 * @returns All legal references indexed by ID
 */
export function getAllLegalRefs(): Record<string, LegalReference> {
  return ALL_LEGAL_REFS;
}

// Re-export individual references for direct import
export {
  // DüV
  DUEV_3,
  DUEV_4_1,
  DUEV_4_4,
  DUEV_6,
  DUEV_6_8,
  DUEV_10_1,
  DUEV_10_2,
  DUEV_LEGAL_REFS,
  // StoffBilV
  STOFFBILV_3,
  STOFFBILV_6,
  STOFFBILV_7,
  STOFFBILV_LEGAL_REFS,
  // Plant Protection
  PFLSCHG_9,
  PFLSCHG_11,
  PFLSCHSACHKV_1,
  PFLSCHSACHKV_7,
  PFLANZENSCHUTZ_LEGAL_REFS,
  // Equipment & Safety
  STVZO_29,
  BETRSICHV_3,
  BETRSICHV_14,
  ARBSCHG_12,
  ENERGIESTG_57,
  EQUIPMENT_SAFETY_LEGAL_REFS,
};
